"use client";

import { useCallback, useEffect, useState } from "react";
import {
  AdminAuthError,
  adminFetch,
  setAdminKey,
} from "@/lib/admin-client";
import { Badge, Button, Card, Spinner, inputClass } from "@/components/ui";

interface LedgerEntry {
  id: number;
  delta: number;
  reason: string;
  note?: string;
  createdAt: string;
}

interface AdminUser {
  id: string;
  email: string;
  plan: string;
  balanceCents: number;
  projects: number;
  videos: number;
  succeeded: number;
  amountSpentCents: number;
  createdAt: string | null;
  ledger: LedgerEntry[];
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [needKey, setNeedKey] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [delta, setDelta] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await adminFetch<{ users: AdminUser[] }>("/api/admin/users");
      setUsers(r.users);
      setNeedKey(false);
    } catch (e) {
      if (e instanceof AdminAuthError) setNeedKey(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function adjust(userId: string) {
    const amountCents = Math.round(Number(delta) * 100);
    if (!amountCents) {
      setNotice("Enter a non-zero USD amount, e.g. 10.00 or -3.50");
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const r = await adminFetch<{ balanceCents: number }>("/api/admin/users", {
        method: "POST",
        body: JSON.stringify({ userId, amountCents, note }),
      });
      setNotice(`Done — new balance $${(r.balanceCents / 100).toFixed(2)}`);
      setDelta("");
      setNote("");
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Adjustment failed");
    } finally {
      setBusy(false);
    }
  }

  async function mergeDuplicate(source: AdminUser, target: AdminUser) {
    const finalBalance = source.balanceCents;
    if (!window.confirm(
      `Merge the newer ${source.email} identity into the original account?\n\n` +
      `Projects, drafts, videos, and uploads will move to the original account. ` +
      `The final cash balance will be $${(finalBalance / 100).toFixed(2)} (the newer identity's balance).`,
    )) return;
    setBusy(true);
    setNotice(null);
    try {
      const r = await adminFetch<{
        balanceCents: number;
        counts: { projects: number; drafts: number; jobs: number };
      }>("/api/admin/users/merge", {
        method: "POST",
        body: JSON.stringify({
          sourceUserId: source.id,
          targetUserId: target.id,
          expectedEmail: source.email,
          targetBalanceCents: finalBalance,
          confirm: "MERGE_ACCOUNTS",
        }),
      });
      setNotice(
        `Merged — moved ${r.counts.projects} projects, ${r.counts.drafts} drafts, ` +
        `${r.counts.jobs} videos; final balance $${(r.balanceCents / 100).toFixed(2)}`,
      );
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Account merge failed");
    } finally {
      setBusy(false);
    }
  }

  if (needKey) {
    return (
      <div className="mx-auto max-w-sm px-5 py-24">
        <Card className="p-6">
          <h1 className="text-lg font-semibold">Admin key</h1>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            className={`${inputClass} mt-4`}
            placeholder="ADMIN_KEY"
          />
          <Button
            className="mt-3 w-full"
            onClick={() => {
              setAdminKey(keyInput);
              void load();
            }}
          >
            Unlock
          </Button>
        </Card>
      </div>
    );
  }

  if (!users) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-muted">
        <Spinner className="size-6" />
      </div>
    );
  }

  const duplicateGroups = Object.values(
    users.reduce<Record<string, AdminUser[]>>((groups, user) => {
      const email = user.email.trim().toLowerCase();
      (groups[email] ??= []).push(user);
      return groups;
    }, {}),
  ).filter((group) => group.length > 1);

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:py-10">
      <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
      <p className="mt-2 text-sm text-muted">
        Everyone who has signed in (demo mode shows the single demo account).
      </p>

      {duplicateGroups.map((group) => {
        const ordered = [...group].sort((a, b) =>
          new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime()
        );
        const target = ordered[0];
        const source = ordered.at(-1)!;
        return (
          <Card key={target.email} className="mt-6 border-amber-500/30 p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="text-sm font-semibold text-amber-300">
                  Duplicate login identities found
                </div>
                <div className="mt-1 text-xs text-muted">
                  {target.email}: keep the original account from {target.createdAt
                    ? new Date(target.createdAt).toLocaleDateString("en-US")
                    : "—"}, move the newer account's data, and keep its
                  {" $"}{(source.balanceCents / 100).toFixed(2)} balance.
                </div>
              </div>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => void mergeDuplicate(source, target)}
              >
                {busy ? <Spinner /> : "Merge duplicate"}
              </Button>
            </div>
          </Card>
        );
      })}

      {notice && (
        <div className="mt-4 rounded-xl border border-line px-4 py-3 text-[13px] text-muted">
          {notice}
        </div>
      )}

      {users.map((u) => (
        <div key={u.id}>
          <Card className="mt-8 p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-semibold">{u.email}</span>
                  <Badge>{u.plan}</Badge>
                </div>
                <div className="mt-1 text-[12px] text-muted">
                  Since{" "}
                  {u.createdAt
                    ? new Date(u.createdAt).toLocaleDateString("en-US")
                    : "—"}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-6 text-center">
                {(
                  [
                    ["Balance", `$${(u.balanceCents / 100).toFixed(2)}`],
                    ["Projects", u.projects],
                    ["Videos", u.videos],
                    ["Spent", `$${(u.amountSpentCents / 100).toFixed(2)}`],
                  ] as [string, string | number][]
                ).map(([label, value]) => (
                  <div key={label}>
                    <div className="text-xl font-semibold">{value}</div>
                    <div className="text-[11px] text-muted">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-end gap-3 border-t border-line pt-5">
              <div>
                <div className="mb-1.5 text-[12px] text-muted">
                  Adjust cash balance in USD (+/-)
                </div>
                <input
                  value={delta}
                  onChange={(e) => setDelta(e.target.value)}
                  className={`${inputClass} w-28`}
                  placeholder="+10.00"
                />
              </div>
              <div className="min-w-52 flex-1">
                <div className="mb-1.5 text-[12px] text-muted">Note</div>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className={inputClass}
                  placeholder="Reason (shows in the user's billing history)"
                />
              </div>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => void adjust(u.id)}
              >
                {busy ? <Spinner /> : "Apply"}
              </Button>
            </div>
            {notice && (
              <div className="mt-3 text-[13px] text-muted">{notice}</div>
            )}
          </Card>

          <h2 className="mt-8 mb-3 text-sm font-medium text-muted">
            Recent ledger
          </h2>
          <Card className="divide-y divide-[var(--border)] p-0">
            {u.ledger.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between gap-4 px-5 py-3"
              >
                <div className="min-w-0">
                  <span className="text-[13px] capitalize">{e.reason}</span>
                  <span className="ml-2 truncate text-[12px] text-muted">
                    {e.note ?? ""}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-[11px] text-muted">
                    {new Date(e.createdAt).toLocaleString("en-US")}
                  </span>
                  <span
                    className={
                      e.delta > 0
                        ? "font-mono text-[13px] text-[var(--success)]"
                        : "font-mono text-[13px]"
                    }
                  >
                    {e.delta > 0 ? "+" : "-"}${(Math.abs(e.delta) / 100).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </Card>
        </div>
      ))}
    </div>
  );
}
